import { Pipe, PipeTransform } from '@angular/core';
import { Utils } from 'app/custom-class/utils';

/** Every shape this pipe can produce, each backed by `Utils`' `Intl`-derived
 * pattern builders — never a format string hardcoded to one language's
 * convention. */
export type LocalizedDateMode =
	| 'date' // dd/MM/yyyy
	| 'dateTime' // dd/MM/yyyy HH:mm
	| 'dateTimeSeconds' // dd/MM/yyyy HH:mm:ss
	| 'time' // HH:mm
	| 'timeSeconds' // HH:mm:ss
	| 'dayMonth' // dd/MM
	| 'dayMonthTime' // dd/MM HH:mm
	| 'year' // yyyy
	| 'shortDate' // dd/MM/yy
	| 'long' // EEEE d MMMM yyyy
	| 'longTime'; // EEEE d MMMM yyyy, HH:mm

const FORMAT_BUILDERS: Record<LocalizedDateMode, () => string> = {
	date: () => Utils.getDateFormat(),
	dateTime: () => `${Utils.getDateFormat()} ${Utils.getTimeFormat()}`,
	dateTimeSeconds: () => `${Utils.getDateFormat()} ${Utils.getTimeFormat(true)}`,
	time: () => Utils.getTimeFormat(),
	timeSeconds: () => Utils.getTimeFormat(true),
	dayMonth: () => Utils.getDayMonthFormat(),
	dayMonthTime: () => `${Utils.getDayMonthFormat()} ${Utils.getTimeFormat()}`,
	year: () => Utils.getYearFormat(),
	shortDate: () => Utils.getShortYearDateFormat(),
	long: () => Utils.getLongDateFormat(),
	longTime: () => `${Utils.getLongDateFormat()}, ${Utils.getTimeFormat()}`,
};

/**
 * Formats a date using the app's active language convention, derived live
 * from `Intl.DateTimeFormat` (see `Utils.getDateFormat()` and friends)
 * instead of a format string hardcoded to one convention regardless of the
 * selected language — including the clock (12-hour with AM/PM for English,
 * 24-hour otherwise) and, for `'long'`/`'longTime'`, the actual field order
 * of a spelled-out date, which is not the same across languages either
 * ("17 settembre 2026" vs "September 17, 2026").
 *
 * Drop-in replacement for the `| date:'...'` template patterns this
 * codebase used to hardcode one format string into:
 *   {{ someDate | localizedDate }}                 (was 'dd/MM/yyyy')
 *   {{ someDate | localizedDate:true }}             (was 'dd/MM/yyyy HH:mm')
 *   {{ someDate | localizedDate:'dateTimeSeconds' }} (was 'dd/MM/yyyy HH:mm:ss')
 *   {{ someDate | localizedDate:'time' }}           (was 'HH:mm')
 *   {{ someDate | localizedDate:'timeSeconds' }}    (was 'HH:mm:ss')
 *   {{ someDate | localizedDate:'dayMonth' }}       (was 'dd/MM')
 *   {{ someDate | localizedDate:'dayMonthTime' }}   (was 'dd/MM HH:mm')
 *   {{ someDate | localizedDate:'year' }}           (was 'YYYY')
 *   {{ someDate | localizedDate:'shortDate' }}      (was 'dd/MM/YY')
 *   {{ someDate | localizedDate:'longTime' }}       (was 'EEEE d MMMM yyyy, HH:mm')
 *
 * `true`/`false` are accepted as shorthand for the two most common modes
 * ('dateTime'/'date') so the existing `| localizedDate:true` call sites
 * don't need touching.
 *
 * `pure: false` so it re-renders on a language change the same way
 * `| translate` does, instead of showing the old format until the next
 * unrelated change-detection pass.
 */
@Pipe({
	name: 'localizedDate',
	pure: false,
	standalone: true,
})
export class LocalizedDatePipe implements PipeTransform {
	transform(value: any, mode: boolean | LocalizedDateMode = 'date', defaultValue = '-'): string {
		const resolvedMode: LocalizedDateMode = mode === true ? 'dateTime' : mode === false ? 'date' : mode;
		const format = FORMAT_BUILDERS[resolvedMode]();
		return Utils.formatDate(value, format, defaultValue);
	}
}
